import { useCallback, useEffect, useRef, useState } from 'react';
import useLocalize from '../../utils/hooks/useLocalize';
import useRangePositioning from '../../utils/hooks/useRangePositioning';
import './RangeSelector.scss';

const RangeSelector = ({
  values = [],
  unit = '',
  minLabel = 'desktop.scans.rangeSelector.default',
  maxLabel = 'desktop.scans.rangeSelector.default',
  onChange,
  disabled = false
}) => {
  const [minValue, setMinValue] = useState(null);
  const [maxValue, setMaxValue] = useState(null);
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);
  const minHandleRef = useRef(null);
  const maxHandleRef = useRef(null);
  const localize = useLocalize();

  // Configuration constants
  const HISTOGRAM_WIDTH = 312;
  const BAR_WIDTH = 3;
  const BAR_SPACING = 1;
  const HISTOGRAM_LEFT_OFFSET = 10;

  // Use custom hook for positioning logic
  const {
    dataBounds,
    histogramData,
    pixelToConstrainedValue,
    pixelPositions,
    isValidMinValue,
    isValidMaxValue
  } = useRangePositioning({
    values,
    histogramWidth: HISTOGRAM_WIDTH,
    barWidth: BAR_WIDTH,
    barSpacing: BAR_SPACING,
    histogramLeftOffset: HISTOGRAM_LEFT_OFFSET,
    minValue,
    maxValue
  });

  const dataMin = dataBounds?.min ?? 0;
  const dataMax = dataBounds?.max ?? 100;
  const minPosPx = pixelPositions?.minPosPx ?? 0;
  const maxPosPx = pixelPositions?.maxPosPx ?? 0;

  // Handle mouse/touch events for dragging
  const handlePointerDown = (type) => (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(type);
  };

  const handlePointerMove = useCallback(
    (e) => {
      if (
        !isDragging ||
        !sliderRef.current ||
        disabled ||
        !pixelToConstrainedValue
      )
        return;

      const trackEl = sliderRef.current.querySelector('.slider-track');
      const rect = trackEl
        ? trackEl.getBoundingClientRect()
        : sliderRef.current.getBoundingClientRect();
      const x = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const newValue = pixelToConstrainedValue(x, rect);

      if (
        isDragging === 'min' &&
        isValidMinValue &&
        isValidMinValue(newValue)
      ) {
        setMinValue(newValue);
        if (onChange) {
          onChange({ min: newValue, max: maxValue });
        }
      } else if (
        isDragging === 'max' &&
        isValidMaxValue &&
        isValidMaxValue(newValue)
      ) {
        setMaxValue(newValue);
        if (onChange) {
          onChange({ min: minValue, max: newValue });
        }
      }
    },
    [
      isDragging,
      minValue,
      maxValue,
      pixelToConstrainedValue,
      isValidMinValue,
      isValidMaxValue,
      onChange,
      disabled
    ]
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove);
      document.addEventListener('touchend', handlePointerUp);

      return () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  // Handle text input changes - store raw input during typing
  const [minInputValue, setMinInputValue] = useState('');
  const [maxInputValue, setMaxInputValue] = useState('');

  // Sync input display values with actual values
  useEffect(() => {
    setMinInputValue(minValue !== null ? String(minValue) : '');
  }, [minValue]);

  useEffect(() => {
    setMaxInputValue(maxValue !== null ? String(maxValue) : '');
  }, [maxValue]);

  const handleMinInputChange = (e) => {
    const value = e.target.value;
    setMinInputValue(value);

    if (value === '') {
      setMinValue(null);
      if (onChange) {
        onChange({ min: null, max: maxValue });
      }
      return;
    }

    const numValue = parseFloat(value.replace(/,/g, ''));
    if (!isNaN(numValue) && isValidMinValue && isValidMinValue(numValue)) {
      setMinValue(numValue);
      if (onChange) {
        onChange({ min: numValue, max: maxValue });
      }
    }
  };

  const handleMaxInputChange = (e) => {
    const value = e.target.value;
    setMaxInputValue(value);

    if (value === '') {
      setMaxValue(null);
      if (onChange) {
        onChange({ min: minValue, max: null });
      }
      return;
    }

    const numValue = parseFloat(value.replace(/,/g, ''));
    if (!isNaN(numValue) && isValidMaxValue && isValidMaxValue(numValue)) {
      setMaxValue(numValue);
      if (onChange) {
        onChange({ min: minValue, max: numValue });
      }
    }
  };

  return (
    <div className={`range-selector-container ${disabled ? 'disabled' : ''}`}>
      {/* Slider visualization */}
      <div className="slider-visualization">
        {/* Histogram background */}
        <div className="histogram-container">
          {histogramData?.map((height, index) => (
            <div
              key={index}
              className="histogram-bar"
              style={{ height: `${height}%` }}
            />
          )) || null}
        </div>

        {/* Slider track and handles */}
        <div className="slider-container" ref={sliderRef}>
          <div className="slider-track" />
          {/* Connecting bar between handles */}
          <div
            className="slider-connecting-bar"
            style={{
              left: `${minPosPx}px`,
              width: `${maxPosPx - minPosPx}px`
            }}
          />

          {/* Min handle */}
          <div
            ref={minHandleRef}
            className="slider-handle min-handle"
            style={{ left: `${minPosPx}px` }}
            onMouseDown={handlePointerDown('min')}
            onTouchStart={handlePointerDown('min')}
          />

          {/* Max handle */}
          <div
            ref={maxHandleRef}
            className="slider-handle max-handle"
            style={{ left: `${maxPosPx}px` }}
            onMouseDown={handlePointerDown('max')}
            onTouchStart={handlePointerDown('max')}
          />
        </div>
      </div>

      {/* Range labels */}
      <div className="slider-range-labels">
        <span className="label-left">
          {dataMin?.toLocaleString() || '0'}
          {unit}
        </span>
        <span className="label-right">
          {dataMax?.toLocaleString() || '100'}
          {unit}
        </span>
      </div>

      {/* Amount input row */}
      <div className="amount-input-row">
        <input
          type="text"
          className={`amount-input ${minInputValue !== '' ? 'active' : ''}`}
          value={minInputValue}
          placeholder={localize(minLabel)}
          onChange={handleMinInputChange}
          disabled={disabled}
        />
        <span className="amount-separator">〜</span>
        <input
          type="text"
          className={`amount-input ${maxInputValue !== '' ? 'active' : ''}`}
          value={maxInputValue}
          placeholder={localize(maxLabel)}
          onChange={handleMaxInputChange}
          disabled={disabled}
        />
        {unit && <span className="amount-unit">{unit}</span>}
      </div>
    </div>
  );
};

export default RangeSelector;
